"use client";

import { images } from "@/lib/images";

interface Article {
  category: string;
  date: string;
  title: string;
  excerpt: string;
  readTime: string;
  imageKey: keyof typeof images;
}

const ARTICLES: Article[] = [
  {
    category: "Art de vivre",
    date: "12 août 2026",
    title: "Paris vue par OR : 48h dans la capitale du goût",
    excerpt:
      "Du marché des Enfants Rouges à la rue Saint-Honoré, en passant par les ateliers de nos artisans-poissonniers : un itinéraire gourmand à travers la ville qui inspire Hyper OR.",
    readTime: "6 min de lecture",
    imageKey: "journal:paris",
  },
  {
    category: "Rencontre",
    date: "8 août 2026",
    title: "Chef Aminata Faye : « Cuisiner, c'est raconter la France »",
    excerpt:
      "Notre chef conseil reçoit chaque semaine une classe de jeunes apprenties. Rencontre avec une femme qui transmet l'âme de la cuisine française.",
    readTime: "8 min de lecture",
    imageKey: "journal:chef",
  },
  {
    category: "Terroir",
    date: "5 août 2026",
    title: "Le miel de Provence : l'or des forêts françaises",
    excerpt:
      "Reportage chez nos apiculteurs partenaires, où la biodiversité offre un miel d'une rare complexité, désormais en rayon chez Hyper OR I.",
    readTime: "5 min de lecture",
    imageKey: "journal:miel",
  },
];

export function JournalSection() {
  return (
    <section id="journal" className="relative bg-cream py-24 md:py-32 border-t border-ink/8">
      <div className="mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="ornament-line mb-6">
              <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
                Le Journal OR
              </span>
            </div>
            <h2 className="font-display text-ink text-4xl md:text-6xl leading-[1.05]">
              Histoires,
              <br />
              <span className="gold-text italic">savoir-faire &amp; goûts.</span>
            </h2>
          </div>
          <a
            href="#journal"
            className="inline-flex items-center gap-2 text-ink hover:text-gold text-[11px] tracking-[0.25em] uppercase border-b border-gold/40 pb-1 hover:border-gold transition-colors"
          >
            Tous les articles
          </a>
        </div>

        {/* Articles grid */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {ARTICLES.map((a, idx) => {
            const imgSrc = images[a.imageKey] as string | undefined;
            return (
            <article
              key={idx}
              className="group relative bg-white border border-ink/8 rounded-2xl overflow-hidden hover:luxury-shadow transition-all duration-500 hover:-translate-y-1 flex flex-col"
            >
              {/* Visual */}
              <div className="relative aspect-[4/3] overflow-hidden">
                {imgSrc && (
                  <img
                    src={imgSrc}
                    alt={a.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold bg-ink text-cream">
                    {a.category}
                  </span>
                </div>
                <div className="absolute bottom-3 right-3 text-[10px] tracking-[0.15em] uppercase text-ink/55 bg-white/90 backdrop-blur px-2 py-0.5 rounded-full">
                  {a.date}
                </div>
              </div>
              {/* Body */}
              <div className="flex-1 flex flex-col p-6">
                <h3 className="font-display text-ink text-xl leading-tight">
                  {a.title}
                </h3>
                <p className="mt-3 text-ink/60 text-sm leading-relaxed font-serif">
                  {a.excerpt}
                </p>
                <div className="mt-auto pt-5 border-t border-ink/8 flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-ink/45">
                    {a.readTime}
                  </span>
                  <span className="inline-flex items-center gap-2 text-gold text-[11px] tracking-[0.25em] uppercase border-b border-gold/40 pb-0.5 group-hover:gap-3 transition-all">
                    Lire
                  </span>
                </div>
              </div>
            </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
